import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { verifyResetOtp, resendResetOtp } from '../../Services/APIs/authentication';
import useOtpCountdown from './useOtpCountdown';
import useFlowState from './useFlowState';
import AuthLayout from './AuthLayout';

const OTP_EXPIRY_SECONDS = 10 * 60;
// backend allows one code per account per 60s, shared with forgot-password — so start armed
const RESEND_COOLDOWN_SECONDS = 60;

const errorClasses = "text-xs text-cv2-destructive mt-1";

const ResetPassword = () => {
    const navigate = useNavigate();
    const { email } = useFlowState('clinicPasswordReset', ['email'], '/forgot-password');

    const [submitError, setSubmitError] = useState('');
    const [resendMessage, setResendMessage] = useState('');
    const [isResending, setIsResending] = useState(false);
    const { formatted, reset: resetCountdown, expired } = useOtpCountdown(OTP_EXPIRY_SECONDS);
    const { secondsLeft: resendCooldown, reset: resetResendCooldown } = useOtpCountdown(RESEND_COOLDOWN_SECONDS);

    const initialValues = { otp: '' };

    const validationSchema = Yup.object().shape({
        otp: Yup.string().required('OTP is required'),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitError('');
        try {
            await verifyResetOtp({ authenticate_email: email, otp: values.otp });
            navigate('/new-password', { state: { email, otp: values.otp } });
        } catch (error) {
            setSubmitError(error?.response?.data?.message || error?.data?.message || 'Something went wrong. Please try again.');
        }
        setSubmitting(false);
    };

    const handleResend = async () => {
        setResendMessage('');
        setIsResending(true);
        try {
            const response = await resendResetOtp({ authenticate_email: email });
            setResendMessage(response.data?.message || 'A password reset code has been sent to your email.');
            resetCountdown();
            resetResendCooldown();
        } catch (error) {
            setResendMessage(error?.response?.data?.message || error?.data?.message || 'Could not resend code. Please try again.');
        }
        setIsResending(false);
    };

    return (
        <AuthLayout variant="signin">

                <div className="flex-1 flex flex-col justify-center">
                    <div className="mb-12">
                        <p className="cv2-auth-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-3.5">
                            Account Recovery
                        </p>
                        <h1 className="font-aeonik font-normal text-[38px] leading-[44px] tracking-[-0.9px] text-cv2-foreground normal-case mb-3">
                            Enter Reset<br />Code
                        </h1>
                        <p className="cv2-auth-subcopy-strong font-aeonik text-sm normal-case">
                            We've sent a code to <span className="text-cv2-foreground">{email || 'your email'}</span>
                        </p>
                    </div>

                    {submitError && (
                        <div className="cv2-auth-error-banner text-sm border rounded-md px-3 py-2 mb-4">
                            {submitError}
                        </div>
                    )}

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting }) => (
                            <Form className="flex flex-col">
                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="otp" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        Verification Code
                                    </label>
                                    <Field
                                        type="text"
                                        name="otp"
                                        inputMode="numeric"
                                        maxLength={6}
                                        placeholder="000000"
                                        className="cv2-auth-input w-full bg-transparent border-0 outline-none font-aeonik text-[15px] tracking-[0.3em] text-cv2-foreground normal-case"
                                    />
                                    <ErrorMessage name="otp" component="div" className={errorClasses} />
                                    <div className={`font-spaceMono text-[9px] tracking-[1.2px] uppercase mt-2 ${expired ? 'text-cv2-destructive' : 'cv2-auth-hint'}`}>
                                        {expired ? 'Code Expired' : `Expires in ${formatted}`}
                                    </div>
                                </div>

                                <div className="cv2-auth-row border-t" />

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="mt-7 w-full h-12 bg-claraview-secondary hover:brightness-110 disabled:opacity-50 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase"
                                >
                                    Verify Code
                                </button>

                                <button
                                    type="button"
                                    disabled={isResending || resendCooldown > 0}
                                    onClick={handleResend}
                                    className="cv2-auth-resend mt-4 w-full text-center font-aeonik text-[13px] normal-case underline underline-offset-2 disabled:no-underline disabled:cursor-not-allowed"
                                >
                                    {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                                </button>

                                {resendMessage && (
                                    <div className="cv2-auth-hint text-xs mt-2 text-center normal-case font-aeonik">
                                        {resendMessage}
                                    </div>
                                )}
                            </Form>
                        )}
                    </Formik>
                </div>

                <Link to="/login" className="cv2-auth-footer mt-10 text-center font-aeonik text-[13px] normal-case block">
                    ← Back to Login
                </Link>
        </AuthLayout>
    );
};

export default ResetPassword;
